// API de Agenda (Citas) — Visum Oftalmologia.
// Backend real en http://<host>:8030, endpoints /citas, /espacios,
// /procedimientos-quirurgicos, /tipos-consentimiento (ver
// app/routers/agenda.py, catalogos_agenda.py del backend). No hay un
// interceptor global que agregue el Authorization header (solo hay uno de
// refresh de sesion en axiosSetup.js), asi que cada llamada lo manda a mano,
// mismo patron que src/redux/actions/login/login.js.
import axios from "axios";
import { PATH } from "../redux/utils";

const BASE_URL = `${PATH}`;

const authHeader = () => {
  const token = localStorage.getItem("token");
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

// ---------------- citas ----------------

export async function listCitas(filtros = {}) {
  const params = {};
  if (filtros.fecha_inicio) params.fecha_inicio = filtros.fecha_inicio;
  if (filtros.fecha_fin) params.fecha_fin = filtros.fecha_fin;
  if (filtros.estado) params.estado = filtros.estado;
  if (filtros.id_paciente) params.id_paciente = filtros.id_paciente;
  if (filtros.id_user_doctor) params.id_user_doctor = filtros.id_user_doctor;

  const { data } = await axios.get(`${BASE_URL}/citas`, {
    headers: { ...authHeader() },
    params,
  });
  return data; // { items, count }
}

export async function getCita(idCita) {
  const { data } = await axios.get(`${BASE_URL}/citas/${idCita}`, {
    headers: { ...authHeader() },
  });
  return data;
}

export async function createCita(payload) {
  const { data } = await axios.post(`${BASE_URL}/citas`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { id, item }
}

export async function updateCita(idCita, payload) {
  const { data } = await axios.put(`${BASE_URL}/citas/${idCita}`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { item }
}

export async function cancelarCita(idCita, motivo, resolucionAnticipo) {
  const { data } = await axios.post(
    `${BASE_URL}/citas/${idCita}/cancelar`,
    { motivo, resolucion_anticipo: resolucionAnticipo || undefined },
    { headers: { ...authHeader(), "Content-Type": "application/json" } }
  );
  return data; // { item }
}

export async function reagendarCita(idCita, payload) {
  const { data } = await axios.post(`${BASE_URL}/citas/${idCita}/reagendar`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { id, item, cita_anterior }
}

export async function crearSeguimiento(idCita, payload) {
  const { data } = await axios.post(`${BASE_URL}/citas/${idCita}/seguimiento`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { id, item }
}

// ---------------- autorizacion menor / cirugia (1:1) ----------------

export async function upsertAutorizacionMenor(idCita, payload) {
  const { data } = await axios.put(`${BASE_URL}/citas/${idCita}/autorizacion-menor`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { item }
}

export async function upsertCirugia(idCita, payload) {
  const { data } = await axios.put(`${BASE_URL}/citas/${idCita}/cirugia`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { item }
}

// ---------------- participantes ----------------

export async function listParticipantes(idCita) {
  const { data } = await axios.get(`${BASE_URL}/citas/${idCita}/participantes`, {
    headers: { ...authHeader() },
  });
  return data; // []
}

export async function addParticipante(idCita, payload) {
  const { data } = await axios.post(`${BASE_URL}/citas/${idCita}/participantes`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { id, participantes }
}

export async function removeParticipante(idCita, idCitaParticipante) {
  const { data } = await axios.delete(
    `${BASE_URL}/citas/${idCita}/participantes/${idCitaParticipante}`,
    { headers: { ...authHeader() } }
  );
  return data; // { participantes }
}

// ---------------- medios (fotos/video) ----------------

export async function listMedios(idCita) {
  const { data } = await axios.get(`${BASE_URL}/citas/${idCita}/medios`, {
    headers: { ...authHeader() },
  });
  return data; // []
}

// tipo/nota_contexto van como query params (asi los declara el backend, no
// son Form(...) en el endpoint multipart), el archivo va en el FormData.
export async function addMedio(idCita, { tipo, notaContexto, file }) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${BASE_URL}/citas/${idCita}/medios`, form, {
    headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
    params: { tipo, nota_contexto: notaContexto || undefined },
  });
  return data; // { id, medios }
}

export async function deleteMedio(idCita, idMedio) {
  const { data } = await axios.delete(`${BASE_URL}/citas/${idCita}/medios/${idMedio}`, {
    headers: { ...authHeader() },
  });
  return data; // { medios }
}

// ---------------- consentimientos ----------------

export async function listConsentimientos(idCita) {
  const { data } = await axios.get(`${BASE_URL}/citas/${idCita}/consentimientos`, {
    headers: { ...authHeader() },
  });
  return data; // []
}

export async function createConsentimiento(idCita, payload) {
  const { data } = await axios.post(`${BASE_URL}/citas/${idCita}/consentimientos`, payload, {
    headers: { ...authHeader(), "Content-Type": "application/json" },
  });
  return data; // { id, consentimientos }
}

export async function firmarConsentimiento(idCita, idConsentimiento, firmadoPor) {
  const { data } = await axios.post(
    `${BASE_URL}/citas/${idCita}/consentimientos/${idConsentimiento}/firmar`,
    { firmado_por: firmadoPor },
    { headers: { ...authHeader(), "Content-Type": "application/json" } }
  );
  return data; // { consentimientos }
}

// ---------------- catalogos de lectura ----------------

export async function listEspacios(activeOnly = true) {
  const { data } = await axios.get(`${BASE_URL}/espacios`, {
    headers: { ...authHeader() },
    params: { active_only: activeOnly },
  });
  return data; // []
}

export async function listProcedimientosQuirurgicos(activeOnly = true) {
  const { data } = await axios.get(`${BASE_URL}/procedimientos-quirurgicos`, {
    headers: { ...authHeader() },
    params: { active_only: activeOnly },
  });
  return data; // []
}

export async function listTiposConsentimiento(activeOnly = true) {
  const { data } = await axios.get(`${BASE_URL}/tipos-consentimiento`, {
    headers: { ...authHeader() },
    params: { active_only: activeOnly },
  });
  return data; // []
}

// ---------------- catalogos externos (pacientes / doctores) ----------------

// Modulo Pacientes ya tiene su router real (/pacientes?search=), aunque su
// pantalla propia (nuevo-modulo-estilo-productos) todavia no se construye —
// se usa aqui solo como buscador para elegir paciente al agendar.
export async function searchPacientes(search) {
  const { data } = await axios.get(`${BASE_URL}/pacientes`, {
    headers: { ...authHeader() },
    params: search ? { search } : {},
  });
  return data?.items || [];
}

export async function getPaciente(idPaciente) {
  const { data } = await axios.get(`${BASE_URL}/pacientes/${idPaciente}`, {
    headers: { ...authHeader() },
  });
  return data;
}

// /usuarios (para elegir doctor) exige rol Administrador (ver
// usuarios_admin.py: require_admin). Si el usuario logueado no es admin este
// call regresa 403 y el selector de doctor cae a un input numerico simple
// (ver AgendaPage/CitaFormModal) en vez de romper la pantalla.
export async function listUsuariosAdmin(search) {
  const { data } = await axios.get(`${BASE_URL}/usuarios`, {
    headers: { ...authHeader() },
    params: search ? { search } : {},
  });
  return data?.items || [];
}
